import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'

export interface NdtInspector {
  id: string
  first_name: string
  last_name: string
  full_name: string
}

export interface NdtAdmin {
  id: string
  full_name: string
}

export interface NdtCompany {
  id: string
  name: string
  report_code: string | null
}

export interface NdtCurrentUser {
  id: string
  full_name: string
  role: string
  supervisor: boolean
}

export function useNdtPersonnel() {
  const [inspectors, setInspectors] = useState<NdtInspector[]>([])
  const [admins, setAdmins] = useState<NdtAdmin[]>([])
  const [companies, setCompanies] = useState<NdtCompany[]>([])
  const [currentUser, setCurrentUser] = useState<NdtCurrentUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const { data: { user } } = await supabase.auth.getUser()

        const [inspectorResult, adminResult, companiesResult, profileResult] = await Promise.all([
          supabase.from('profiles').select('id, full_name').eq('role', 'inspector').order('full_name'),
          supabase.from('profiles').select('id, full_name').eq('role', 'admin').order('full_name'),
          supabase.from('companies').select('id, name, report_code').order('name'),
          user
            ? supabase.from('profiles').select('id, full_name, role, supervisor').eq('id', user.id).single()
            : Promise.resolve({ data: null, error: null }),
        ])

        if (inspectorResult.data) {
          setInspectors(
            inspectorResult.data.map((row) => ({
              id: row.id,
              first_name: row.full_name?.split(' ')[0] || '',
              last_name: row.full_name?.split(' ').slice(1).join(' ') || '',
              full_name: row.full_name || '',
            })),
          )
        }

        if (adminResult.data) {
          setAdmins(
            adminResult.data
              .filter((row): row is { id: string; full_name: string } => !!row.full_name)
              .map((row) => ({ id: row.id, full_name: row.full_name })),
          )
        }

        if (companiesResult.data) setCompanies(companiesResult.data)

        if (profileResult.data) {
          const profile = profileResult.data
          const adminAccess = profile.role === 'admin' || profile.supervisor === true
          setIsAdmin(adminAccess)
          setCurrentUser({
            id: profile.id,
            full_name: profile.full_name || '',
            role: profile.role,
            supervisor: profile.supervisor === true,
          })
        }
      } catch (error) {
        console.error('Error loading NDT form users:', error)
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [])

  const isInspectorLocked = currentUser?.role === 'inspector' && !isAdmin

  return {
    inspectors,
    admins,
    companies,
    currentUser,
    loading,
    isAdmin,
    isInspectorLocked,
  }
}
